import { productApi } from "#/api/productApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import SelectableTable from "#/components/tables/SelectableTable";
import { EmptyState } from "#/components/ui/EmptyState";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import { exportToCSV } from "#/utils/CsvUtils";
import { Button, Skeleton, Table, Text, TextInput, Title } from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { IconCopyPlus, IconDownload, IconEye, IconPlus, IconSearch, IconTrash, IconUpload } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import ImportCSVModal from "./components/ImportCSVModal";
import BulkDeleteCategoriesModal from "./components/BulkDeleteCategoriesModal";
import type { Product } from "#/types/products";

const ProductsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [
    importCsvModalOpened,
    { open: openImportCsvModal, close: closeImportCsvModal },
  ] = useDisclosure(false);

  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);

  const { data: { products = [], total = 0, per_page = 0 } = {}, isFetching } =
    useQuery({
      queryKey: ["products", page, limit, debouncedSearch],
      queryFn: async () => {
        const response = await productApi.getAll({
          page: page,
          limit: limit,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        if (!response.data) {
          throw new Error("Failed to fetch categories");
        }
        return {
          products: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      },
      enabled: true,
      staleTime: 1000 * 60,
    });

  const headers = [
    { key: "name", value: t("products.columns.name") },
    { key: "sku", value: t("products.form.sku") },
    { key: "variant", value: t("products.columns.variant") },
    { key: "cost", value: t("products.columns.cost") },
    { key: "retail_price", value: t("products.columns.retailPrice") },
    { key: "filling_capacity", value: t("products.columns.fillingCapacity") },
    { key: "tare_weight", value: t("products.columns.tareWeight") },
    { key: "number_of_items", value: t("products.columns.numberOfItems") },
    { key: "number_of_sites", value: t("products.columns.numberOfSites") },
    { key: "actions", value: t("common.actions") },
  ];

  const prepareExportData = (productsToExport: typeof products) => {
    return productsToExport.map((product) => ({
      name: product.name || "",
      sku: product.sku || "",
      variant: product.variant || "",
      cost: product.cost || "",
      retail_price: product.retail_price || "",
      filling_capacity: product.filling_capacity || "",
      tare_weight: product.tare_weight || "",
      number_of_items: product.number_of_items ?? 0,
      number_of_sites: product.number_of_sites ?? 0,
    }));
  };

  const handleExportCurrentPage = () => {
    if (!products || products.length === 0) return;

    const exportData = prepareExportData(products);
    const exportHeaders = [
      "name",
      "sku",
      "variant",
      "cost",
      "retail_price",
      "filling_capacity",
      "tare_weight",
      "number_of_items",
      "number_of_sites",
    ];

    const headerLabels = {
      name: "Name",
      sku: "SKU",
      variant: "Variant",
      cost: "Cost",
      retail_price: "Retail Price",
      filling_capacity: "Filling Capacity",
      tare_weight: "Tare Weight",
      number_of_items: "# of Items",
      number_of_sites: "# of Sites",
    };

    const filename = `categories-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, headerLabels);
  };

  const handleExportAll = async () => {
    try {
      const response = await productApi.getAll({
        page: 1,
        limit: total,
      });

      const allProducts = response.data.data;
      const exportData = prepareExportData(allProducts);

      const exportHeaders = [
        "name",
        "sku",
        "variant",
        "cost",
        "retail_price",
        "filling_capacity",
        "tare_weight",
        "number_of_items",
      ];

      const headerLabels = {
        name: "Name",
        sku: "SKU",
        variant: "Variant",
        cost: "Cost",
        retail_price: "Retail Price",
        filling_capacity: "Filling Capacity",
        tare_weight: "Tare Weight",
        number_of_items: "# of Items",
      };

      const filename = `categories-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, headerLabels);
    } catch (error) {
      console.error("Failed to export all categories:", error);
    }
  };

  const handleImportSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ["products"] });
  };

  const clearSelection = () => {
    setSelectedIndices([]);
    setSelectedProducts([]);
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-2 relative">
      <div className="w-full flex flex-col gap-3">
        <div className="w-full flex items-center justify-between">
          <Title order={4}>{t("products.title")}</Title>
          <div className="gap-2 flex items-center ml-auto">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                onClick={() => {
                  void navigate("/inventory/categories/new");
                }}
                leftSection={<IconPlus size={18} />}
                color="black"
                disabled={!canWrite()}
              >
                {t("products.addCategory")}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconUpload size={18} />}
                color="green.5"
                onClick={openImportCsvModal}
                disabled={!canWrite()}
              >
                {t("common.importCsv")}
              </Button>
            </PermissionTooltip>
            <Button
              leftSection={<IconDownload size={18} />}
              variant="outline"
              onClick={openExportModal}
              disabled={!products || products.length === 0}
            >
              {t("common.exportCsv")}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-between">
          <TextInput
            className="flex-1 max-w-96"
            placeholder={t("products.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
              clearSelection();
            }}
          />
          <div className="flex items-center gap-2 ml-auto">
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={(newPage) => {
                setPage(newPage);
                clearSelection();
              }}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
                clearSelection();
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isFetching && (
          <div className="w-full grid grid-cols-4 gap-4">
            {Array.from({ length: 8 }, (_, i) => ({ id: `dummy-${i}` })).map(
              (item) => (
                <Skeleton key={item.id} height={150} />
              )
            )}
          </div>
        )}

        {!isFetching && products.length <= 0 && (
          <div className="w-full h-64">
            <EmptyState
              title={t("products.noDataTitle")}
              description={t("products.noDataDescription")}
              cta={
                <PermissionTooltip disabled={!canWrite()}>
                  <Button
                    onClick={() => navigate("/inventory/categories/new")}
                    disabled={!canWrite()}
                  >
                    {t("products.addCategory")}
                  </Button>
                </PermissionTooltip>
              }
            />
          </div>
        )}

        {!isFetching && products.length > 0 && (
          <SelectableTable
            headers={headers.map((item) => item.value)}
            loading={isFetching}
            selectedIndices={selectedIndices}
            onSelectionChange={(newIndices) => {
              const selected = newIndices.map((index) => products[index]);
              setSelectedProducts(selected);
              setSelectedIndices(newIndices);
            }}
            rows={products.map((item) => {
              return (
                <Table.Tr key={item.id}>
                  <Table.Td style={{ minWidth: '200px' }}>{item.name ?? "--"}</Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>{item.sku ?? "--"}</Table.Td>
                  <Table.Td style={{ minWidth: '150px' }}>{item.variant ?? "--"}</Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>{item.cost ?? "--"}</Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>
                    {item.retail_price ?? "--"}
                  </Table.Td>
                  <Table.Td style={{ minWidth: '150px' }}>
                    {item.filling_capacity ?? "--"}
                  </Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>
                    {item.tare_weight ?? "--"}
                  </Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>
                    {item.number_of_items ?? 0}
                  </Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>
                    {item.number_of_sites ?? 0}
                  </Table.Td>
                  <Table.Td style={{ minWidth: '120px' }}>
                    <div className="w-full flex items-center justify-center gap-2">
                      <PermissionTooltip disabled={!canWrite()}>
                        <ActionButton
                          icon={IconCopyPlus}
                          variant="outline"
                          color="purple"
                          disabled={!canWrite()}
                          onClick={() => {
                            void navigate(`/inventory/categories/new?duplicateFrom=${item.id}`);
                          }}
                        />
                      </PermissionTooltip>
                      <ActionButton
                        icon={IconEye}
                        variant="outline"
                        onClick={() => {
                          void navigate(`/inventory/categories/${item.id}`);
                        }}
                      />
                    </div>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          />
        )}
      </div>

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={products.length}
        totalCount={total}
      />

      <ImportCSVModal
        opened={importCsvModalOpened}
        onClose={closeImportCsvModal}
        onSuccess={handleImportSuccess}
      />

      <BulkDeleteCategoriesModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedProducts={selectedProducts}
        onSuccess={() => {
          clearSelection();
          void queryClient.invalidateQueries({ queryKey: ["products"] });
        }}
      />

      {selectedProducts.length > 0 && (
        <div className="absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedProducts.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={openBulkDeleteModal}
                disabled={!canDelete()}
              >
                {t("common.delete")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
